import type {Module} from "vuex";
import type {RootState} from "@/store/types";
import type {Language} from "./i18n";
import type {PermissionRestrictions} from "./user/permissions";
import {platform} from "@/api/platform";
import {order} from "@/api/order";
import {delivery} from "@/api/delivery";
import {auth} from "@/api/auth";
import {retryRequest} from "@/helpers/request";
import {isAuthServiceEnabled} from "@/helpers/auth";
import {ORDERS_LIMIT, PERMISSIONS} from "@/constants";

// Modules
import address from "./user/address";
import banners from "./user/banners";
import cart from "./user/cart";
import catalog from "./user/catalog";
import constraints from "./user/constraints";
import delegate from "./user/delegate";
import impersonation from "./user/impersonation";
import orders from "./user/orders";
import permissions from "./user/permissions";
import profile from "./user/profile";
import quiz from "./user/quiz";
import surveys from "./user/surveys";

export interface ImpersonatorToken {
    id: string
    token: string
}

// Authenticated user data passed to fetch() after login
export interface UserFetchPayload {
    token: string
    id: string
    stylistTags?: string[] | null
    requireAcknowledgement?: unknown
    permissions: {
        permissions: string[]
        restrictions: PermissionRestrictions
    }
}

export interface UserLogoutOptions {
    force?: boolean
}

export interface ConsumerResponse {
    preferred_language?: string
    acknowledgement?: unknown
    approval_manager_email?: string | null
}

export interface UserState {
    token: string | null
    impersonatorToken: ImpersonatorToken | null
    id: string | null
    stylistTags: string[] | null
    language: string | null
    acknowledgement: unknown
    requireAcknowledgement: unknown
    approvalManagerEmail: string | null
    loaded: boolean
}

const user: Module<UserState, RootState> = {
    namespaced: true,
    state: {
        token: null,
        impersonatorToken: null,
        id: null,
        stylistTags: null,
        language: null,
        acknowledgement: null,
        requireAcknowledgement: null,
        approvalManagerEmail: null,
        loaded: false
    },
    getters: {
        isAuthenticated: (state): boolean => !!state.token,
        id: (state): string | null => state.id,
        token: (state): string | null => state.token,
        impersonatorToken: (state): ImpersonatorToken | null => state.impersonatorToken,
        stylistTags: (state): string[] => state.stylistTags ? state.stylistTags : [],
        language: (state): string | null => state.language,
        acknowledgement: (state): unknown => state.acknowledgement,
        requireAcknowledgement: (state): unknown => state.requireAcknowledgement,
        approvalManagerEmail: (state): string | null => state.approvalManagerEmail,
        loaded: (state): boolean => state.loaded
    },
    mutations: {
        setToken: (state, token: string | null) => state.token = token,
        setImpersonatorToken: (state, impersonatorToken: ImpersonatorToken | null) => state.impersonatorToken = impersonatorToken,
        setId: (state, id: string | null) => state.id = id,
        setStylistTags: (state, stylistTags: string[] | null) => state.stylistTags = stylistTags,
        setLanguage: (state, language: string | null) => state.language = language,
        setAcknowledgement: (state, acknowledgement: unknown) => state.acknowledgement = acknowledgement,
        setRequireAcknowledgement: (state, requireAcknowledgement: unknown) => state.requireAcknowledgement = requireAcknowledgement,
        setApprovalManagerEmail: (state, approvalManagerEmail: string | null) => state.approvalManagerEmail = approvalManagerEmail,
        setLoaded: (state, loaded: boolean) => state.loaded = loaded
    },
    actions: {
        // Loads everything the app needs for the (just) authenticated user
        fetch(context, data?: UserFetchPayload) {

            if (data) {

                context.commit("setToken", data.token);
                context.commit("setId", data.id);
                context.commit("setStylistTags", data.stylistTags);
                context.commit("setRequireAcknowledgement", data.requireAcknowledgement);

                this.dispatch("user/permissions/setPermissions", data.permissions.permissions);
                this.dispatch("user/permissions/setRestrictions", data.permissions.restrictions);
            }

            platform.disableUnauthorizedInterceptor();
            order.disableUnauthorizedInterceptor();
            delivery.disableUnauthorizedInterceptor();
            auth.disableUnauthorizedInterceptor();

            const promises = [
                retryRequest(() => platform.get<{results: ConsumerResponse}>(`/api/v2/consumers/${context.state.id}`, {params: {get: "preferred_language,acknowledgement,approval_manager_email"}})),
                this.dispatch("config/fetch"),
                this.dispatch("user/profile/fetch"),
            ];

            return Promise
                .all(promises)
                .then(responses => {

                    this.dispatch("user/surveys/fetch");
                    this.dispatch("user/banners/fetch");

                    const [consumerResponse] = responses;
                    const language = consumerResponse.data.results.preferred_language;
                    const acknowledgement = consumerResponse.data.results.acknowledgement;
                    const approvalManagerEmail = consumerResponse.data.results.approval_manager_email

                    context.commit("setLanguage", language);
                    context.commit("setAcknowledgement", acknowledgement);
                    context.commit("setApprovalManagerEmail", approvalManagerEmail);
                    context.commit("setLoaded", true);

                    const promises = [this.dispatch("i18n/fetch")];

                    if (
                        (
                            this.getters["user/profile/country"]
                            && this.getters["user/profile/equipment_submitted"]
                            && this.getters["user/permissions/hasPermissionAccess"](PERMISSIONS.EMPLOYEE)
                        )
                        || this.getters["user/permissions/hasPermissionAccess"](PERMISSIONS.MANAGER)
                    ) {

                        promises.push(this.dispatch("user/orders/fetch", {
                            skip: 0,
                            limit: ORDERS_LIMIT,
                            transferred: "false"
                        }));
                    }

                    if (
                        this.getters["user/profile/country"]
                        && this.getters["user/profile/equipment_submitted"]
                        && this.getters["user/permissions/hasPermissionAccess"](PERMISSIONS.EMPLOYEE)
                    ) {

                        promises.push(this.dispatch("user/cart/fetch"));
                        promises.push(this.dispatch("user/constraints/fetch"));
                        promises.push(this.dispatch("user/constraints/fetchCart"));
                    }

                    return Promise
                        .all(promises)
                        .then(
                            () => {

                                if (language) {

                                    this.dispatch(
                                        "i18n/setLanguage",
                                        this.getters["i18n/languages"].find((lang: Language) => lang.value === language)
                                    );
                                }
                            }
                        );
                })
                .finally(() => {

                    platform.enableUnauthorizedInterceptor();
                    order.enableUnauthorizedInterceptor();
                    delivery.enableUnauthorizedInterceptor();
                    auth.enableUnauthorizedInterceptor();
                })
        },
        // Saves the preferred language of the user (or of the impersonator)
        setLanguage(context, language: string | null) {

            context.commit("setLanguage", language);

            if (context.getters["isAuthenticated"]) {

                const impersonatorToken = context.getters["impersonatorToken"];
                const request = {preferred_language: language};

                return !impersonatorToken
                    ? platform.put(`/api/v2/consumers/${context.state.id}`, request)
                    : platform.put(`/api/v2/consumers/${impersonatorToken.id}`, request, {
                        headers: {Authorization: impersonatorToken.token}
                    });
            }
        },
        setAcknowledgement(context, acknowledgement: unknown) {

            context.commit("setAcknowledgement", acknowledgement);

            return platform.put(`/api/v2/consumers/${context.state.id}`, {acknowledgement});
        },
        // force: also invalidate the token on the server
        logout(context, options: UserLogoutOptions = {force: true}) {

            if (true === options.force) {

                if (isAuthServiceEnabled()) {

                    auth.delete(`/api/login/jwt`);
                } else {

                    platform.post(`/api/v2/users/${context.state.id}/jwt/logout`);
                }
            }

            context.commit("setId", null);
            context.commit("setToken", null);
            context.commit("setStylistTags", null);
            context.commit("setLanguage", null);

            this.dispatch("config/user/clear");
            this.dispatch("user/profile/clear");
            this.dispatch("user/address/clear");
            this.dispatch("user/cart/clear");
            this.dispatch("user/constraints/clear");
            this.dispatch("user/quiz/profile/clear");
            this.dispatch("user/surveys/clear");
            this.dispatch("user/permissions/clear");
        }
    },
    modules: {
        address,
        banners,
        cart,
        catalog,
        constraints,
        delegate,
        impersonation,
        orders,
        permissions,
        profile,
        quiz,
        surveys
    }
}

export default user
